import { ModalService } from "@allbesmart/web-app-common";
import {
  UserRole,
  WebAppKeycloakAuthModule,
} from "@allbesmart/web-app-keycloak-auth";
import { DatePipe, UpperCasePipe } from "@angular/common";
import { Component, Input, OnChanges } from "@angular/core";
import { ORDER_TYPE_CONFIG } from "src/app/configs/order-type-config";
import { OrderType } from "src/app/enums/order-type";
import { OrderDto } from "src/app/models/order-dto";
import { OperationItemModalComponent } from "../../operations/operation-item-modal/operation-item-modal.component";
import { OrderTenantAssignmentComponent } from "../../operations/order-tenant-assignment/order-tenant-assignment.component";
import { OrderTrackingDetailsConfigurationComponent } from "../../operations/order-tracking-details-configuration/order-tracking-details-configuration.component";
import { DeleteOrderWarningModalComponent } from "../delete-order-warning-modal/delete-order-warning-modal.component";
import { OrderBillOfMaterialsComponent } from "../order-bill-of-materials/order-bill-of-materials.component";
import { OrderLogsModalComponent } from "../order-logs-modal/order-logs-modal.component";
import { OrderNotesModalComponent } from "../order-notes-modal/order-notes-modal.component";

@Component({
  selector: "oaibox-order-row",
  templateUrl: "./order-row.component.html",
  styleUrl: "./order-row.component.css",
  imports: [WebAppKeycloakAuthModule, DatePipe, UpperCasePipe],
})
export class OrderRowComponent implements OnChanges {
  @Input({ required: true }) order!: OrderDto;

  protected readonly OrderType = OrderType;
  protected readonly UserRole = UserRole;

  protected dueDateIsOverdue: boolean = false;
  protected isBringupDone: boolean = false;
  protected orderStateLabel?: string;
  protected fulfilmentSteps: FulfilmentStep[] = [];
  protected notesCount: number = 0;

  constructor(private readonly modalService: ModalService) {}

  ngOnChanges(): void {
    const orderTypes = Object.values(OrderType);
    const orderType = this.order.orderType;
    const currentIndex = orderType ? orderTypes.indexOf(orderType) : -1;
    const isBehindReceivedByCustomer =
      currentIndex < orderTypes.indexOf(OrderType.RECEIVED_BY_CUSTOMER);

    this.isBringupDone = orderType === OrderType.BRINGUP_DONE;
    this.notesCount = this.order.notes?.length ?? 0;
    this.dueDateIsOverdue =
      !!this.order.dueDate &&
      isBehindReceivedByCustomer &&
      new Date(this.order.dueDate).getTime() < Date.now();
    this.orderStateLabel = orderType
      ? ORDER_TYPE_CONFIG[orderType].label
      : undefined;
    this.fulfilmentSteps = orderType
      ? orderTypes.map((type, index) => ({
          type,
          label: ORDER_TYPE_CONFIG[type].label,
          reached: index <= currentIndex,
        }))
      : [];
  }

  protected openOrderNotesModal(): void {
    this.modalService.pushModal({
      component: OrderNotesModalComponent,
      size: "medium",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }

  protected openOperationItemModal(): void {
    this.modalService.pushModal({
      component: OperationItemModalComponent,
      size: "medium",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }

  protected openOrderLogsModal(): void {
    this.modalService.pushModal({
      component: OrderLogsModalComponent,
      size: "medium",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }

  protected openDeleteOrderModal(): void {
    this.modalService.pushModal({
      component: DeleteOrderWarningModalComponent,
      inputs: new Map<string, OrderDto>([["order", this.order]]),
      closeButtonColor: "white",
    });
  }

  protected openBillOfMaterialsModal(): void {
    this.modalService.pushModal({
      component: OrderBillOfMaterialsComponent,
      size: "small",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }

  protected openOrderTenantAssignmentModal(): void {
    this.modalService.pushModal({
      component: OrderTenantAssignmentComponent,
      size: "medium",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }

  protected openOrderTrackingDetailsConfigurationModal(): void {
    this.modalService.pushModal({
      component: OrderTrackingDetailsConfigurationComponent,
      size: "medium",
      inputs: new Map<string, OrderDto>([["order", this.order]]),
    });
  }
}

type FulfilmentStep = {
  type: OrderType;
  label: string;
  reached: boolean;
};
